import { createContext, Dispatch } from 'react';
import type { AuthUser } from 'aws-amplify/auth';
import { FileData, ForeignNational, Role } from 'API';

export const APP_AUTH_STATES = {
  SignIn: 'signIn',
  SignedIn: 'signedIn',
  SignedOut: 'signedOut',
} as const;

type UserAction =
  | {
    type: 'login',
    authState: AppAuthState,
    authData: AuthUser,
    person?: Person | null,
    role?: Role | null,
    accessCompanies?: Array<string>,
    adminCompanies?: Array<string>,
    dependents?: Array<string>, // FNs this user has admin access to
    isUserVisibilityToggleOn?: boolean,
  }
  | { type: 'logout' }
  | { type: 'update person', person: Person }
  | { type: 'add person file', file: FileData }
  | { type: 'spoof role', role: Role }
  | { type: 'set visibility toggle state', isUserVisibilityToggleOn: boolean };

export const initialAuthState = {
  authState: APP_AUTH_STATES.SignedOut,
  user: null,
  person: null,
  role: null,
  accessCompanies: [],
  adminCompanies: [],
  dependents: [],
  isUserVisibilityToggleOn: false,
};

export const authReducer = (
  state: UserState, action: UserAction,
): UserState => {
  switch (action.type) {
    case 'login':
      return {
        authState: action.authState,
        user: action.authData,
        person: action.person || null,
        role: action.role || null,
        accessCompanies: action.accessCompanies || [],
        adminCompanies: action.adminCompanies || [],
        dependents: action.dependents || [],
        isUserVisibilityToggleOn: action.isUserVisibilityToggleOn || false,
      };
    case 'logout':
      return {
        authState: APP_AUTH_STATES.SignedOut,
        user: null,
        person: null,
        role: null,
        accessCompanies: [],
        adminCompanies: [],
        dependents: [],
        isUserVisibilityToggleOn: false,
      };
    case 'update person':
      return {
        ...state,
        person: action.person,
      };
    // Appends to the *current* person, not a snapshot captured by the caller: a
    // multi-file upload finishes several files in quick succession, and building
    // the list from a stale `person` kept only the last one (VOYAG-803).
    case 'add person file': {
      if (!state.person) {
        return state;
      }

      const person = state.person as ForeignNational;

      return {
        ...state,
        person: {
          ...person,
          files: { ...person.files, items: [...(person.files?.items || []), action.file] },
        } as Person,
      };
    }
    case 'spoof role':
      return {
        ...state,
        role: action.role,
      };
    case 'set visibility toggle state':
      return {
        ...state,
        isUserVisibilityToggleOn: action.isUserVisibilityToggleOn,
      };
    default:
      return state;
  }
};

export const UserContext = createContext<[UserState, Dispatch<UserAction>]>([
  {
    authState: APP_AUTH_STATES.SignedOut,
    user: null,
    person: null,
    role: null,
    accessCompanies: [],
    adminCompanies: [],
    dependents: [],
    isUserVisibilityToggleOn: false,
  },
  () => {},
]);

export default UserContext;
